import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  ORGANIZATION_PERMISSION_SLUGS,
  buildUnifiedSearchPayload,
  getDefaultUnifiedOrderSearchSourceFilters,
} from '@blocktickets/shared-utils'

import UserContext from '../../../context/User/User'
import { usePagination } from '../../../providers/PaginationProvider/PaginationProvider'

import useAbortableFetch from '../../../utilities/useAbortableFetch'
import AuthService from '../../../utilities/services/auth.service'

import { formatNumber, checkPermission } from './../../../utilities/helpers'

import { unifiedSearch } from '../../../utilities/api'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'

import { OrderSummary } from './OrderSummary'

import { LoadingContainer } from '../../LoadingContainer'
import { Pagination } from '../../Pagination'
import SearchCard from './SearchCard/SearchCard'

import './ordersList.scss'

const DEFAULT_INPUT_ERRORS = {
  searchTerm: false,
  eventOrPackage: false,
}

export default function OrdersList({ organization }) {
  const { orgPermissions } = useContext(UserContext)

  const canManageOrders = useMemo(() => {
    const orgPermissionsReady = orgPermissions?.length > 0
    return (
      orgPermissionsReady &&
      checkPermission(
        AuthService.getPermissions(),
        ORGANIZATION_PERMISSION_SLUGS.ORDERS_MANAGEMENT
      )
    )
  }, [orgPermissions])

  const [orders, setOrders] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedEventOrPackage, setSelectedEventOrPackage] = useState(null)
  const [totalOrders, setTotalOrders] = useState(0)
  const [sourceFilters, setSourceFilters] = useState(
    getDefaultUnifiedOrderSearchSourceFilters
  )

  // Hooks for convenience
  const abortable = useAbortableFetch()

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const runSearch = useCallback(
    async (newPage, searchTerm, section, row, seat) => {
      setIsLoading(true)

      try {
        const res = await abortable((signal) =>
          unifiedSearch(
            buildUnifiedSearchPayload({
              organizationUUID: organization?.uuid,
              selectedEventOrPackage,
              searchTerm,
              sectionNumber: section,
              rowNumber: row,
              seatNumbers: seat,
              sourceFilters,
              page: newPage + 1,
              perPage,
            }),
            signal
          )
        )

        const payload = res?.data
        if (!payload?.meta?.pagination) {
          setIsLoading(false)
          return
        }

        const {
          data,
          meta: {
            pagination: { pageCount: pc, page, total },
          },
        } = payload

        setCurrentPage(page - 1)
        setOrders(data)
        setPageCount(pc)
        setTotalOrders(formatNumber(total))
        setIsLoading(false)
      } catch (err) {
        const aborted =
          err?.name === 'CanceledError' ||
          err?.name === 'AbortError' ||
          err?.code === 'ERR_CANCELED'
        if (aborted) {
          return
        }
        console.error(err)
        setIsLoading(false)
      }
    },
    [
      abortable,
      currentPage,
      perPage,
      selectedEventOrPackage,
      setPageCount,
      sourceFilters,
    ]
  )

  const handleFilterChange = (value) => {
    if (value === '') {
      setSelectedEventOrPackage(null)
    } else {
      setSelectedEventOrPackage(JSON.parse(value))
    }
  }

  useEffect(() => {
    runSearch(currentPage)
  }, [perPage])

  const triggerSeatSearch = (seatSearchData) => {
    if (seatSearchData.seatSearch) {
      const { section, row, seat } = seatSearchData
      runSearch(0, undefined, section, row, seat)
    } else {
      const { searchTerm } = seatSearchData
      runSearch(0, searchTerm, undefined, undefined)
    }
  }

  return (
    <Stack
      gap={4}
      id="search-orders"
      data-testid="orders-list-content"
      data-orders-management={canManageOrders ? 'allowed' : 'denied'}
    >
      <SearchCard
        organization={organization}
        handleFilterChange={handleFilterChange}
        triggerSeatSearch={triggerSeatSearch}
        selectedEventOrPackage={selectedEventOrPackage}
        sourceFilters={sourceFilters}
        setSourceFilters={setSourceFilters}
        isLoading={isLoading}
      />

      <OrderInfo totalOrders={totalOrders} isLoading={isLoading} />

      <Results
        isLoading={isLoading}
        orders={orders}
        canManageOrders={canManageOrders}
        pagination={{
          currentPage,
          setCurrentPage,
          perPage,
          pageCount,
          setPageCount,
          resultsPerPageOpts,
          handleChange,
        }}
        runSearch={runSearch}
      />
    </Stack>
  )
}

export function OrderInfo({ isLoading, totalOrders }) {
  return (
    <Stack
      direction="horizontal"
      className="w-100 px-2 justify-content-between align-items-end"
    >
      <ul>
        <li>
          <p className="small mb-2 small-bold">Order Status Legend</p>
          <Stack as="ul" direction="horizontal" className="horizontal-list">
            <li className="info-text info-text--sm info-text-square info-text-has-tickets">
              <span>Tickets available to user</span>
            </li>
            <li className="info-text info-text--sm info-text-square info-text-reserved">
              <span>Pending send</span>
            </li>
            <li className="info-text info-text--sm info-text-square info-text-released">
              <span>Tickets released</span>
            </li>
          </Stack>
        </li>
      </ul>
      {!isLoading && (
        <span className="text-muted text-small">
          Orders found: {totalOrders}
        </span>
      )}
    </Stack>
  )
}

function Results({
  isLoading,
  orders,
  canManageOrders,
  pagination,
  runSearch,
}) {
  if (isLoading) {
    return <LoadingContainer />
  }

  if (orders?.length === 0) {
    return (
      <Card body>
        <Stack
          gap={3}
          className="justify-center align-items-center"
          id="empty-container"
        >
          <h1 className="normal normal-bold m-0">
            No results for your search criteria
          </h1>
          <p className="subtitle subtitle--dark m-0">
            Make sure the information submitted is correct and try again. If
            still nothing shows up, could be that there is no order for your
            search criteria
          </p>
        </Stack>
      </Card>
    )
  }

  return (
    <>
      <Stack as="ul" gap={4}>
        {orders.map((order, i) => (
          <OrderSummary
            key={i}
            order={order}
            runSearch={runSearch}
            canManageOrders={canManageOrders}
          />
        ))}
      </Stack>
      <Pagination
        pageCount={pagination.pageCount}
        perPage={pagination.perPage}
        currentPage={pagination.currentPage}
        resultsPerPageOpts={pagination.resultsPerPageOpts}
        handleClick={(value) => runSearch(value.selected)}
        handleChange={pagination.handleChange}
      />
    </>
  )
}
